import type {
  BillingCycle, CompanyType, DealType, LeadSource, LeadStatus,
  LifecycleStage, QuoteStatus, TaskPriority, TaskStatus, TaskType,
} from './types';

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  QUALIFIED: 'Qualified',
  UNQUALIFIED: 'Not qualified',
  NURTURING: 'Nurturing',
  CONVERTED: 'Converted',
};

/** Badge variants, so a status looks the same everywhere it appears. */
export const LEAD_STATUS_VARIANT: Record<LeadStatus, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
  NEW: 'default',
  CONTACTED: 'secondary',
  QUALIFIED: 'success',
  UNQUALIFIED: 'destructive',
  NURTURING: 'warning',
  CONVERTED: 'success',
};

export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  WEBSITE: 'Website',
  REFERRAL: 'Referral',
  CAMPAIGN: 'Campaign',
  COLD_OUTREACH: 'Cold outreach',
  EVENT: 'Event',
  PARTNER: 'Partner',
  INBOUND_CALL: 'Inbound call',
  WHATSAPP: 'WhatsApp',
  SOCIAL: 'Social media',
  MARKETPLACE: 'Marketplace',
  OTHER: 'Other',
};

export const COMPANY_TYPE_LABELS: Record<CompanyType, string> = {
  PROSPECT: 'Prospect',
  CLIENT: 'Client',
  PARTNER: 'Partner',
  VENDOR: 'Vendor',
  CHURNED: 'Churned',
};

export const LIFECYCLE_LABELS: Record<LifecycleStage, string> = {
  SUBSCRIBER: 'Subscriber',
  LEAD: 'Lead',
  OPPORTUNITY: 'Opportunity',
  CUSTOMER: 'Customer',
  EVANGELIST: 'Evangelist',
  CHURNED: 'Churned',
};

export const DEAL_TYPE_LABELS: Record<DealType, string> = {
  SAAS_SUBSCRIPTION: 'SaaS subscription',
  CUSTOM_DEVELOPMENT: 'Custom development',
  AMC: 'AMC',
  SERVICE: 'Service',
  UPSELL: 'Upsell',
  RENEWAL: 'Renewal',
};

export const BILLING_CYCLE_LABELS: Record<BillingCycle, string> = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  HALF_YEARLY: 'Half-yearly',
  ANNUAL: 'Annual',
  ONE_TIME: 'One-time',
};

export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending approval',
  SENT: 'Sent',
  VIEWED: 'Viewed',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  REVISED: 'Superseded',
};

export const QUOTE_STATUS_VARIANT: Record<QuoteStatus, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
  DRAFT: 'secondary',
  PENDING_APPROVAL: 'warning',
  SENT: 'default',
  VIEWED: 'default',
  ACCEPTED: 'success',
  REJECTED: 'destructive',
  EXPIRED: 'secondary',
  REVISED: 'secondary',
};

export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  CALL: 'Call',
  EMAIL: 'Email',
  MEETING: 'Meeting',
  FOLLOW_UP: 'Follow-up',
  DEMO: 'Demo',
  TODO: 'To-do',
};

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  URGENT: 'Urgent',
};

export const TASK_PRIORITY_VARIANT: Record<TaskPriority, 'secondary' | 'default' | 'warning' | 'destructive'> = {
  LOW: 'secondary',
  MEDIUM: 'default',
  HIGH: 'warning',
  URGENT: 'destructive',
};

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  DONE: 'Done',
  CANCELLED: 'Cancelled',
};

/** A lead score band, so the number means something at a glance. */
export function scoreBand(score: number): { label: string; className: string } {
  if (score >= 70) return { label: 'Hot', className: 'bg-emerald-100 text-emerald-700' };
  if (score >= 40) return { label: 'Warm', className: 'bg-amber-100 text-amber-700' };
  return { label: 'Cold', className: 'bg-neutral-200 text-neutral-600' };
}
